import { Injectable } from '@angular/core';
import { FilterMetadata } from 'primeng/api';

export interface GridState {
  filters: Record<string, FilterMetadata | FilterMetadata[]>;
  sortField?: string;
  sortOrder?: 1 | -1;
}

/** Central registry of grid state keys — register new grids here. Keeps every key in one
 *  auditable place, and the union type below turns a stateKey typo in a template into a
 *  compile error instead of a silently separate (or shared) state bucket. */
export const GRID_STATE_KEYS = {
  requests: 'requests',
  suppliers: 'suppliers',
  users: 'users',
  approvalMatrixRequests: 'approval-matrix-requests',
  approvalMatrixSections: 'approval-matrix-sections',
  forms: 'forms',
  auditLog: 'audit-log',
} as const;

export type GridStateKey = (typeof GRID_STATE_KEYS)[keyof typeof GRID_STATE_KEYS];

/** Deep-copies state so the store never shares references with the live p-table,
 *  which mutates its filters in place. Preserves the Date values date filters use. */
function deepClone<T>(value: T): T {
  if (value instanceof Date) return new Date(value) as T;
  if (Array.isArray(value)) return value.map((item) => deepClone(item)) as T;
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = deepClone(v);
    return out as T;
  }
  return value;
}

/** In-memory filter/sort state per grid, keyed by each grid's stateKey, so list views restore
 *  their last state when the user navigates back. Deliberately not backed by session/local
 *  storage: state must survive in-app navigation only — refresh, new tab and new session start
 *  clean, and logout clears it explicitly. */
@Injectable({ providedIn: 'root' })
export class GridStateService {
  private readonly states = new Map<GridStateKey, GridState>();

  save(key: GridStateKey, state: GridState): void {
    this.states.set(key, deepClone(state));
  }

  get(key: GridStateKey): GridState | null {
    const state = this.states.get(key);
    return state ? deepClone(state) : null;
  }

  clear(key: GridStateKey): void {
    this.states.delete(key);
  }

  clearAll(): void {
    this.states.clear();
  }
}
